import { useEffect, useState } from "react";
import { Mail, Phone, Facebook, Instagram, ChevronDown, MessageCircle, Twitter, Youtube } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import PageHero from "@/components/PageHero";

const DEFAULT_PHONES = ["+91 84205 42712", "+91 70032 86491", "+91 87770 85121", "+91 98744 24239"];

const FAQS = [
  { q: "How can I register for a SPORTIVE event?", a: "Select an upcoming event and click Register Now. Fill the registration form and you will receive a confirmation with your registration ID." },
  { q: "Are SPORTIVE events only for professional runners?", a: "No. Events include categories for experienced runners, amateur runners, beginners and recreational participants depending on the event." },
  { q: "What is RUNSAFARI?", a: "RUNSAFARI is SPORTIVE's destination-running concept combining running, travel, exploration, culture and local experiences." },
  { q: "Can I participate from another city?", a: "Yes. Many RUNSAFARI events are designed specifically to attract participants from outside the destination." },
  { q: "Does SPORTIVE organise marathon events?", a: "Yes. SPORTIVE conceptualises and manages marathon, half marathon, 10K, 5K, trail and destination-based running experiences." },
  { q: "Can a tourism department partner with SPORTIVE?", a: "Yes. SPORTIVE can develop destination-based sporting and sports tourism projects in collaboration with appropriate organisations." },
  { q: "How can a brand become a sponsor?", a: "Contact SPORTIVE through the partnership section — our team will share sponsorship opportunities for upcoming events." },
];

export default function Contact() {
  const [s, setS] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", type: "General", organization: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    api.get("/settings/public").then((r) => setS(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Message sent! We'll get back to you soon.");
      setForm({ name: "", email: "", phone: "", type: "General", organization: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero eyebrow="CONTACT" title="Let's Run Together."
        subtitle="Whether you are a runner, destination, tourism authority, brand, organisation or potential partner — we'd love to hear from you."
        image="https://images.unsplash.com/photo-1452626038306-9aae5e071dd3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwzfHxtYXJhdGhvbiUyMHJ1bm5lcnMlMjByYWNlJTIwc3RhcnR8ZW58MHx8fHwxNzg5MDY4MTkwfDA&ixlib=rb-4.1.0&q=85" />

      <section className="mx-auto grid max-w-7xl gap-12 px-4 py-20 sm:px-6 lg:grid-cols-5">
        <div className="lg:col-span-2">
          <p className="eyebrow">SPORTIVE</p>
          <h2 className="mt-2 font-display text-3xl font-bold uppercase text-white">Sports Tourism • Marathons • Offbeat Destinations</h2>
          <div className="mt-8 space-y-4">
            <a href={`mailto:${s?.contact_email || "contact@sportive.co.in"}`} data-testid="contact-email" className="card-k flex items-center gap-4 p-4 transition-colors hover:border-brand-cyan/50">
              <Mail size={18} className="text-brand-cyan" />
              <div><p className="font-mono text-[10px] tracking-[0.2em] text-slate-500">EMAIL — GENERAL, EVENTS & PARTNERSHIPS</p><p className="text-sm font-medium text-white">{s?.contact_email || "contact@sportive.co.in"}</p></div>
            </a>
            <a href={`https://wa.me/${s?.whatsapp || "9179809278329"}`} target="_blank" rel="noreferrer" data-testid="contact-whatsapp" className="card-k flex items-center gap-4 p-4 transition-colors hover:border-brand-olive/50">
              <MessageCircle size={18} className="text-brand-olive" />
              <div><p className="font-mono text-[10px] tracking-[0.2em] text-slate-500">CONTACT / WHATSAPP</p><p className="text-sm font-medium text-white">{s?.phone || "+91-79809278329"}</p></div>
            </a>
            <div className="card-k flex items-center gap-4 p-4">
              <Phone size={18} className="text-brand-cyan" />
              <div><p className="font-mono text-[10px] tracking-[0.2em] text-slate-500">BASED IN</p><p className="text-sm font-medium text-white">{s?.address || "Kolkata, West Bengal, India"}</p></div>
            </div>
            <div className="card-k p-4">
              <p className="font-mono text-[10px] tracking-[0.2em] text-slate-500">EVENT HELPLINES</p>
              <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2">
                {(s?.phones || DEFAULT_PHONES).map((p) => (
                  <a key={p} data-testid={`helpline-${p.replace(/\D/g, "")}`} href={`tel:${p.replace(/[^+\d]/g, "")}`} className="text-sm font-medium text-white transition-colors hover:text-brand-cyan">{p}</a>
                ))}
              </div>
            </div>
          </div>
          <p className="eyebrow mt-10">FOLLOW SPORTIVE</p>
          <div className="mt-4 flex gap-3">
            <a data-testid="contact-facebook" href={s?.facebook || "https://facebook.com/runsafari"} target="_blank" rel="noreferrer" className="rounded-full border border-brand-line p-3 text-slate-400 transition-colors hover:border-brand-cyan hover:text-brand-cyan"><Facebook size={18} /></a>
            <a data-testid="contact-instagram" href={s?.instagram || "https://instagram.com/udsportive"} target="_blank" rel="noreferrer" className="rounded-full border border-brand-line p-3 text-slate-400 transition-colors hover:border-brand-olive hover:text-brand-olive"><Instagram size={18} /></a>
            <a data-testid="contact-twitter" href={s?.twitter || "https://x.com/udsportive"} target="_blank" rel="noreferrer" title="X (Twitter)" className="rounded-full border border-brand-line p-3 text-slate-400 transition-colors hover:border-brand-cyan hover:text-brand-cyan"><Twitter size={18} /></a>
            <a data-testid="contact-youtube" href={s?.youtube || "https://www.youtube.com/@SPORTIVERUNSAFARI"} target="_blank" rel="noreferrer" title="YouTube" className="rounded-full border border-brand-line p-3 text-slate-400 transition-colors hover:border-brand-olive hover:text-brand-olive"><Youtube size={18} /></a>
          </div>
        </div>

        <form onSubmit={submit} className="card-k h-fit space-y-4 p-7 lg:col-span-3">
          <p className="eyebrow">SEND A MESSAGE</p>
          <div className="grid gap-4 sm:grid-cols-2">
            <input data-testid="contact-name" required placeholder="Your name" className="input-k" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input data-testid="contact-form-email" required type="email" placeholder="Email" className="input-k" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <input data-testid="contact-phone" placeholder="Phone" className="input-k" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <select data-testid="contact-type" className="input-k" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
              {["General", "Event Query", "Partnership", "Sponsorship", "Media"].map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <textarea data-testid="contact-message" required rows={5} placeholder="Your message..." className="input-k" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
          <button data-testid="contact-submit" disabled={busy} className="btn-kinetic btn-primary-k w-full justify-center disabled:opacity-60">{busy ? "Sending..." : "SEND MESSAGE"}</button>
        </form>
      </section>

      <section id="faq" className="scroll-mt-24 border-t border-brand-line bg-brand-surface/40">
        <div className="mx-auto max-w-3xl px-4 py-20 sm:px-6">
          <p className="eyebrow">FAQ</p>
          <h2 className="mt-3 font-display text-4xl font-bold uppercase tracking-tight text-white">Frequently Asked Questions</h2>
          <div className="mt-10 space-y-3">
            {FAQS.map((f, i) => (
              <div key={i} className="card-k overflow-hidden">
                <button data-testid={`faq-item-${i}`} onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="flex w-full items-center justify-between gap-4 px-6 py-4 text-left text-sm font-semibold text-white">
                  {f.q}
                  <ChevronDown size={16} className={`shrink-0 text-brand-cyan transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && <p className="px-6 pb-5 text-sm leading-relaxed text-slate-400">{f.a}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
